import type { OnDestroy } from '@angular/core';
import { ChangeDetectionStrategy, Component, computed, effect, inject, model, signal, untracked } from '@angular/core';
import type { FieldTree } from '@angular/forms/signals';
import { form } from '@angular/forms/signals';
import { TranslateModule } from '@ngx-translate/core';

import { CSS_RESTRICTIONS } from '@common/constants';
import { DeviceMode } from '@common/types/layout.type';
import { getValidFontSize, getValidLetterSpacing, getValidLineHeight } from '@common/helpers/forms';
import { SpDeviceToggleComponent } from '@components/sp-device-toggle/src/sp-device-toggle.component';
import { SpSynchronizeStyleToggleComponent } from '@components/sp-synchronize-style-toggle/src/sp-synchronize-style-toggle.component';
import { SpInputNumComponent } from '@components/sp-form-controls/sp-input-num/sp-input-num.component';
import { SpColorPickerV2Component, type ColorPickerEventPayload } from '@components/sp-color-picker/src/v2';

import type { ThemeFont, ThemeSelectedFonts } from '@sites/site-theme/src/types/theme-font.type';
import { ThemeFontTags } from '@sites/site-theme/src/enums/font-tags.enum';
import { ThemeFontFamilies } from '@sites/site-theme/src/enums/font-families.enum';
import { ThemeFontProperties } from '@sites/site-theme/src/enums/font-properties.enum';
import { FONT_FAMILIES, FONT_WEIGHT_VALUES } from '@sites/site-theme/src/config/fonts.const';
import { FONT_TAG_TRANSLATIONS, FONT_WEIGHT_TRANSLATIONS } from '@sites/site-theme/src/config/translation-keys.const';
import { HIDDEN_FONT_TAGS } from '@sites/site-theme/src/config/vocabularies.const';
import { THEME_FONT_PROPERTIES } from '@sites/site-theme/src/helpers/font-css';
import type { ThemeBreakpoint } from '@sites/site-theme/src/helpers/responsive';
import { ThemeStateService } from '@sites/site-theme/src/state';

import { fromFontFormValue, toFontFormValue, type FontFormValue } from './font-settings-form';

/** One tag's row in the form — what the template navigates to read and write a property. */
export type FontField = FieldTree<FontFormValue>;

const BREAKPOINT_BY_DEVICE: Readonly<Record<DeviceMode, ThemeBreakpoint>> = {
    [DeviceMode.desktop]: 'desktop',
    [DeviceMode.tablet]: 'tablet',
    [DeviceMode.mobile]: 'mobile',
};

@Component({
    selector: 'sp-theme-fonts-settings',
    templateUrl: './fonts-settings.component.html',
    styleUrls: ['./fonts-settings.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        SpDeviceToggleComponent,
        SpSynchronizeStyleToggleComponent,
        SpInputNumComponent,
        SpColorPickerV2Component,
        TranslateModule,
    ],
})
export class SpThemeFontsSettingsComponent implements OnDestroy {
    private readonly themeState = inject(ThemeStateService);

    /**
     * Two-way: the panel's device tabs change it, and the drawer around it follows.
     *
     * It used to be an `@Input` the component ASSIGNED to before emitting a matching `@Output` —
     * two halves of a two-way binding written out by hand, with the input as local state in between.
     */
    public readonly deviceMode = model<DeviceMode>(DeviceMode.desktop);

    private readonly fonts = signal<FontFormValue[]>([]);
    public readonly fontsForm = form(this.fonts);

    /** Whether the three device tabs share one value. Off lets tablet and mobile diverge. */
    public readonly syncWithDesktop = signal(true);

    public readonly FONTS_TAGS = Object.values(ThemeFontTags);
    public readonly FONT_FAMILY_LIST = Object.values(ThemeFontFamilies);
    public readonly FONT_FAMILIES = FONT_FAMILIES;
    public readonly FONT_WEIGHT_VALUES = FONT_WEIGHT_VALUES;
    public readonly FONT_TAG_TRANSLATIONS = FONT_TAG_TRANSLATIONS;
    public readonly FONT_WEIGHT_TRANSLATIONS = FONT_WEIGHT_TRANSLATIONS;
    public readonly HIDDEN_FONT_TAGS = HIDDEN_FONT_TAGS;
    public readonly themeFontProperties = ThemeFontProperties;
    public readonly BUTTON_TAG = ThemeFontTags.button;

    /** The palette, so the colour picker offers the theme's own swatches. */
    public readonly selectedColors = this.themeState.selectedColors;

    public readonly FONT_SIZE_MIN = CSS_RESTRICTIONS.FONT_SIZE_MIN;
    public readonly FONT_SIZE_MAX = CSS_RESTRICTIONS.FONT_SIZE_MAX;
    public readonly LINE_HEIGHT_MIN = CSS_RESTRICTIONS.LINE_HEIGHT_MIN;
    public readonly LINE_HEIGHT_MAX = CSS_RESTRICTIONS.LINE_HEIGHT_MAX;
    public readonly LETTER_SPACING_MIN = CSS_RESTRICTIONS.LETTER_SPACING_MIN;
    public readonly LETTER_SPACING_MAX = CSS_RESTRICTIONS.LETTER_SPACING_MAX;

    public readonly isDesktopMode = computed(() => this.deviceMode() === DeviceMode.desktop);
    public readonly isTabletMode = computed(() => this.deviceMode() === DeviceMode.tablet);
    public readonly isMobileMode = computed(() => this.deviceMode() === DeviceMode.mobile);

    /** Which device tab's value the accessors below read and write. */
    public readonly currentBreakpoint = computed(() => BREAKPOINT_BY_DEVICE[this.deviceMode()]);

    /** Family and weight are shared across tabs, so they only show while a shared value is in view. */
    public readonly showSharedSettings = computed(() => this.syncWithDesktop() || this.isDesktopMode());

    /** The fonts as they were when the panel opened, for cancel and for closing without saving. */
    private readonly initialFonts = signal<ThemeSelectedFonts | null>(null);
    private isSaved = false;

    constructor() {
        // Seeded once. The form is the editing surface from here on, and re-seeding it from the
        // state it writes to would fight the user's typing.
        effect(() => {
            const themeFonts = this.themeState.fonts();

            if (!themeFonts || untracked(this.initialFonts)) {
                return;
            }

            this.initialFonts.set(themeFonts);
            this.syncWithDesktop.set(themeFonts.syncWithDesktop ?? true);
            this.fonts.set(themeFonts.selected.map(toFontFormValue));
        });
    }

    public tagOf(field: FontField): ThemeFontTags {
        return field.tag().value();
    }

    public familyOf(field: FontField): ThemeFontFamilies {
        return field.font().value();
    }

    /** One property's value on the tab currently in view. */
    public valueOf(field: FontField, property: ThemeFontProperties): string | number | null {
        return field[property]().value()[this.currentBreakpoint()] ?? null;
    }

    public setFontFamily(field: FontField, event: Event): void {
        const family = (event.target as HTMLSelectElement).value as ThemeFontFamilies;

        field.font().value.set(family);
        this.write(field, ThemeFontProperties.fontFamily, FONT_FAMILIES[family].stack, true);
    }

    public setFontWeight(field: FontField, event: Event): void {
        const weight = Number((event.target as HTMLSelectElement).value);

        this.write(field, ThemeFontProperties.fontWeight, weight, true);
    }

    public setFontSize(field: FontField, value: number): void {
        this.write(field, ThemeFontProperties.fontSize, getValidFontSize(value));
    }

    public setLineHeight(field: FontField, value: number): void {
        this.write(field, ThemeFontProperties.lineHeight, getValidLineHeight(value));
    }

    public setLetterSpacing(field: FontField, value: number): void {
        this.write(field, ThemeFontProperties.letterSpacing, getValidLetterSpacing(value));
    }

    /**
     * A palette swatch is stored by its name (`dark`), so the text follows the theme and the picker
     * highlights the swatch; a custom colour is stored as its literal value.
     */
    public colorOf(field: FontField): string {
        return String(this.valueOf(field, ThemeFontProperties.color) ?? '');
    }

    public setColor(field: FontField, { value, presetName }: ColorPickerEventPayload): void {
        this.write(field, ThemeFontProperties.color, presetName ?? value);
    }

    public toggleTextTransform(field: FontField): void {
        const next = this.valueOf(field, ThemeFontProperties.textTransform) === 'uppercase' ? 'none' : 'uppercase';

        this.write(field, ThemeFontProperties.textTransform, next);
    }

    public toggleFontStyle(field: FontField): void {
        const next = this.valueOf(field, ThemeFontProperties.fontStyle) === 'italic' ? 'normal' : 'italic';

        this.write(field, ThemeFontProperties.fontStyle, next);
    }

    /**
     * Turning synchronisation back on copies desktop onto the other two tabs.
     *
     * Turning it off changes nothing: the tabs already hold the same values, and they diverge only
     * once the user edits one.
     */
    public toggleSyncWithDesktop(): void {
        const syncWithDesktop = !this.syncWithDesktop();

        if (syncWithDesktop) {
            this.fonts.update((fonts) =>
                fonts.map((font) => {
                    const next = { ...font };

                    THEME_FONT_PROPERTIES.forEach((property) => {
                        const desktop = font[property].desktop ?? null;

                        next[property] = { desktop, tablet: desktop, mobile: desktop };
                    });

                    return next;
                })
            );
        }

        this.syncWithDesktop.set(syncWithDesktop);
        this.themeState.setFontsSynchronization(syncWithDesktop);
        this.commit();
    }

    public onChangeDeviceMode(mode: DeviceMode): void {
        this.deviceMode.set(mode);
    }

    public onSave(): void {
        this.isSaved = true;
        this.themeState.unselectActiveSetting();
    }

    public onCancel(): void {
        this.restoreInitialFonts();
        this.themeState.unselectActiveSetting();
    }

    public ngOnDestroy(): void {
        if (!this.isSaved) {
            this.restoreInitialFonts();
        }
    }

    /**
     * Writes one property, either on the tab in view or on all three.
     *
     * `synchronizeAll` is on for family and weight — a font cannot have one family per breakpoint —
     * and follows the synchronise toggle for everything else.
     */
    private write(
        field: FontField,
        property: ThemeFontProperties,
        value: string | number,
        synchronizeAll: boolean = this.syncWithDesktop()
    ): void {
        // Nothing binds these inputs through `[formField]`, so nothing marks the form dirty for us.
        this.fontsForm().markAsDirty();

        field[property]().value.update((responsive) =>
            synchronizeAll
                ? { desktop: value, tablet: value, mobile: value }
                : { ...responsive, [this.currentBreakpoint()]: value }
        );

        this.commit();
    }

    /** Pushes the whole form to the theme, which is what repaints the page behind the panel. */
    private commit(): void {
        const rows = this.fonts();
        const pFont = rows.find((row) => row.tag === ThemeFontTags.p);

        // A hidden tag has no controls of its own; it follows body copy, so it is given `p`'s
        // family before being converted back.
        const selected: ThemeFont[] = rows.map((row) =>
            pFont && this.HIDDEN_FONT_TAGS.includes(row.tag)
                ? fromFontFormValue({ ...row, font: pFont.font, fontFamily: pFont.fontFamily })
                : fromFontFormValue(row)
        );

        this.themeState.changeFonts({ selected, selectedIndex: null, syncWithDesktop: this.syncWithDesktop() });
    }

    private restoreInitialFonts(): void {
        const initial = this.initialFonts();

        if (this.fontsForm().dirty() && initial) {
            this.themeState.changeFonts({
                selected: initial.selected,
                selectedIndex: initial.selectedIndex ?? null,
                syncWithDesktop: initial.syncWithDesktop ?? true,
            });
        }
    }
}
